import { Controller } from "@hotwired/stimulus"

// Elternunterhalt-Rechner. Rechengrößen kommen als JSON (ElternunterhaltRechnerController#show),
// compute() spiegelt ElternunterhaltRechner.berechne; die Paritäts-Spec hält beide gleich.
export default class extends Controller {
  static values = { data: Object }

  static targets = [
    "einkommenJahrInput",
    "nettoInput",
    "altersvorsorgeInput",
    "abzuegeInput",
    "familienstandRadio",
    "partnerField",
    "nettoPartnerInput",
    "eigenanteilInput",
    "einkommenElternInput",
    "geschwisterList",
    "geschwisterTemplate",
    "geschwisterRow",
    "geschwisterTitel",
    "geschwisterEntfernen",
    "geschwisterBruttoInput",
    "geschwisterNettoInput",
    "geschwisterZahlung",

    "resultZahlung",
    "resultRange",
    "verdict",
    "warnGrenze",
    "stepLuecke",
    "stepBereinigt",
    "stepAltersvorsorge",
    "stepSelbstbehaltLabel",
    "stepSelbstbehalt",
    "stepLeistungsfaehigkeitInfo",
    "stepLeistungsfaehigkeit",
    "stepZahlung",
    "stepSozialamt",

    "resultSummary",
    "stickyBar",
    "stickyZahlung"
  ]

  connect() {
    this.euro = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 })
    this.euroRund = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 })
    this.naechsteGeschwisterId = 0
    this.calculate()
    this.observeResultCard()
  }

  disconnect() {
    this.observer?.disconnect()
  }

  observeResultCard() {
    if (!this.hasStickyBarTarget || !this.hasResultSummaryTarget || !("IntersectionObserver" in window)) return
    this.observer = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting
      this.stickyBarTarget.classList.toggle("translate-y-full", !show)
      this.stickyBarTarget.inert = !show
    })
    this.observer.observe(this.resultSummaryTarget)
  }

  addGeschwister() {
    const html = this.geschwisterTemplateTarget.innerHTML.replaceAll("__INDEX__", this.naechsteGeschwisterId++)
    this.geschwisterListTarget.insertAdjacentHTML("beforeend", html)
    this.nummeriereGeschwister()
    this.calculate()
    this.geschwisterRowTargets.at(-1).querySelector("input")?.focus()
  }

  removeGeschwister(event) {
    event.currentTarget.closest("[data-elternunterhalt-rechner-target='geschwisterRow']").remove()
    this.nummeriereGeschwister()
    this.calculate()
  }

  nummeriereGeschwister() {
    this.geschwisterRowTargets.forEach((row, i) => {
      this.inRow(row, "geschwisterTitel").textContent = `Geschwister ${i + 1}`
      this.inRow(row, "geschwisterEntfernen").setAttribute("aria-label", `Geschwister ${i + 1} entfernen`)
    })
  }

  inRow(row, target) {
    return row.querySelector(`[data-elternunterhalt-rechner-target='${target}']`)
  }

  calculate() {
    const verheiratet = this.familienstandRadioTargets.find(r => r.checked)?.value === "verheiratet"
    this.partnerFieldTarget.classList.toggle("hidden", !verheiratet)

    const geschwister = this.geschwisterRowTargets.map(row => ({
      einkommenJahr: this.wert(this.inRow(row, "geschwisterBruttoInput")),
      netto: this.wert(this.inRow(row, "geschwisterNettoInput"))
    }))

    const r = this.compute({
      einkommenJahr: this.wert(this.einkommenJahrInputTarget),
      netto: this.wert(this.nettoInputTarget),
      altersvorsorge: this.wert(this.altersvorsorgeInputTarget),
      abzuege: this.wert(this.abzuegeInputTarget),
      verheiratet,
      nettoPartner: verheiratet ? this.wert(this.nettoPartnerInputTarget) : 0,
      eigenanteil: this.wert(this.eigenanteilInputTarget),
      einkommenEltern: this.wert(this.einkommenElternInputTarget),
      geschwister
    })
    this.render(r, verheiratet)
  }

  compute({ einkommenJahr, netto, altersvorsorge, abzuege, verheiratet, nettoPartner, eigenanteil, einkommenEltern, geschwister }) {
    const d = this.dataValue
    const luecke = Math.max(eigenanteil - einkommenEltern, 0)
    const ueberGrenze = einkommenJahr > d.einkommensgrenze
    const avMax = einkommenJahr * d.altersvorsorge_quote / 12.0
    const av = Math.min(Math.max(altersvorsorge, 0), avMax)
    const bereinigt = Math.max(netto - av - abzuege, 0)
    const quote = 1 - d.anteil_frei

    let lfHoch, lfNiedrig, selbstbehalt
    if (verheiratet) {
      const familie = bereinigt + nettoPartner
      const anteilKind = familie > 0 ? bereinigt / familie : 0
      const familienbedarf = d.familienselbstbehalt + Math.max(familie - d.familienselbstbehalt, 0) * (1 - d.haushaltsersparnis) * d.anteil_frei
      const familienmethode = Math.max(bereinigt - familienbedarf * anteilKind, 0)
      const bedarfPartner = Math.max(d.selbstbehalt_ehegatte, familie / 2.0)
      const rest = bereinigt - Math.max(bedarfPartner - nettoPartner, 0)
      const halbteilung = Math.max(rest - d.selbstbehalt_kind, 0) * quote
      lfHoch = Math.max(familienmethode, halbteilung)
      lfNiedrig = Math.min(familienmethode, halbteilung)
      selbstbehalt = d.familienselbstbehalt
    } else {
      lfHoch = lfNiedrig = Math.max(bereinigt - d.selbstbehalt_kind, 0) * quote
      selbstbehalt = d.selbstbehalt_kind
    }

    const lfGeschwister = geschwister.map(g => Math.max(g.netto - d.selbstbehalt_kind, 0) * quote)
    const summeGeschwister = lfGeschwister.reduce((a, b) => a + b, 0)
    const anteil = lf => {
      const summe = lf + summeGeschwister
      return summe > 0 ? Math.min(lf, luecke * lf / summe) : 0
    }
    const zahlung = ueberGrenze ? anteil(lfHoch) : 0
    const zahlungNiedrig = ueberGrenze ? anteil(lfNiedrig) : 0
    const zahlungenGeschwister = geschwister.map((g, i) => {
      if (!(g.einkommenJahr > d.einkommensgrenze)) return 0
      const summe = lfHoch + summeGeschwister
      return summe > 0 ? Math.min(lfGeschwister[i], luecke * lfGeschwister[i] / summe) : 0
    })
    const sozialamt = Math.max(luecke - zahlung - zahlungenGeschwister.reduce((a, b) => a + b, 0), 0)

    return { luecke, ueberGrenze, altersvorsorge: av, bereinigt, selbstbehalt, leistungsfaehigkeit: lfHoch, leistungsfaehigkeitNiedrig: lfNiedrig, zahlung, zahlungNiedrig, zahlungenGeschwister, sozialamt }
  }

  render(r, verheiratet) {
    const d = this.dataValue
    const spanne = verheiratet && r.ueberGrenze && Math.round(r.zahlungNiedrig * 100) !== Math.round(r.zahlung * 100)
    const bereich = (niedrig, hoch) => spanne ? `${this.euro.format(niedrig)} bis ${this.euro.format(hoch)}` : this.euro.format(hoch)

    this.resultZahlungTarget.textContent = this.euro.format(r.zahlung)
    this.resultRangeTarget.classList.toggle("hidden", !spanne)
    if (spanne) this.resultRangeTarget.textContent = `Je nach Berechnungsweise des Gerichts ${bereich(r.zahlungNiedrig, r.zahlung)} pro Monat`
    this.warnGrenzeTarget.classList.toggle("hidden", r.ueberGrenze)
    if (this.hasStickyZahlungTarget) this.stickyZahlungTarget.textContent = this.euro.format(r.zahlung)

    this.stepLueckeTarget.textContent = this.euro.format(r.luecke)
    this.stepAltersvorsorgeTarget.textContent = this.euro.format(r.altersvorsorge)
    this.stepBereinigtTarget.textContent = this.euro.format(r.bereinigt)
    this.stepSelbstbehaltLabelTarget.textContent = verheiratet ? "Familienselbstbehalt (Sie und Ihr Ehepartner)" : "Ihr Selbstbehalt"
    this.stepSelbstbehaltTarget.textContent = this.euro.format(r.selbstbehalt)
    this.stepLeistungsfaehigkeitInfoTarget.textContent = verheiratet
      ? "Familienmethode: Ihr Einkommen über Ihrem Anteil am Familienbedarf; Untergrenze per Halbteilung"
      : `${Math.round((1 - d.anteil_frei) * 100)} % des Einkommens über dem Selbstbehalt, ${Math.round(d.anteil_frei * 100)} % bleiben frei`
    this.stepLeistungsfaehigkeitTarget.textContent = bereich(r.leistungsfaehigkeitNiedrig, r.leistungsfaehigkeit)
    this.stepZahlungTarget.textContent = bereich(r.zahlungNiedrig, r.zahlung)
    this.stepSozialamtTarget.textContent = this.euro.format(r.sozialamt)
    this.geschwisterRowTargets.forEach((row, i) => {
      const ueberGrenze = this.wert(this.inRow(row, "geschwisterBruttoInput")) > d.einkommensgrenze
      this.inRow(row, "geschwisterZahlung").textContent = ueberGrenze
        ? `Über der Grenze: zahlt ${this.euro.format(r.zahlungenGeschwister[i])}`
        : "Unter der Grenze: zahlt nichts, Anteil trägt das Sozialamt"
    })

    this.verdictTarget.textContent = this.verdict(r, spanne, d)
  }

  verdict(r, spanne, d) {
    if (!r.ueberGrenze) return `Mit einem Jahreseinkommen bis ${this.euroRund.format(d.einkommensgrenze)} zahlen Sie keinen Elternunterhalt. Das Sozialamt darf Sie nicht heranziehen.`
    if (r.luecke <= 0) return "Rente und Einkommen Ihres Elternteils decken die Heimkosten. Es entsteht kein Elternunterhalt."
    if (r.zahlung <= 0) return "Ihr bereinigtes Einkommen liegt unter dem Selbstbehalt. Sie zahlen voraussichtlich keinen Elternunterhalt."
    if (spanne && r.zahlungNiedrig <= 0) return `Sie zahlen voraussichtlich bis zu ${this.euro.format(r.zahlung)} pro Monat; je nach Berechnungsweise des Gerichts auch nichts.`
    if (spanne) return `Sie zahlen voraussichtlich zwischen ${this.euro.format(r.zahlungNiedrig)} und ${this.euro.format(r.zahlung)} pro Monat. Den Rest trägt das Sozialamt.`
    if (Math.round(r.sozialamt * 100) <= 0) return `Sie zahlen voraussichtlich ${this.euro.format(r.zahlung)} pro Monat und decken damit die gesamte Lücke.`
    return `Sie zahlen voraussichtlich ${this.euro.format(r.zahlung)} pro Monat. Den Rest von ${this.euro.format(r.sozialamt)} trägt das Sozialamt.`
  }

  // Monat/Jahr-Umschalter: rechnet den angezeigten Wert um, damit der Betrag gleich bleibt
  toggleEinheit(event) {
    const button = event.currentTarget
    const feld = button.closest("[data-basis]")
    const neu = button.dataset.einheit
    if (feld.dataset.einheit === neu) return

    const input = feld.querySelector("input")
    if (input.value.trim() !== "") {
      const betrag = this.num(input)
      input.value = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(neu === "jahr" ? betrag * 12 : betrag / 12)
    }
    feld.dataset.einheit = neu
    feld.querySelectorAll("button[data-einheit]").forEach(btn => {
      const aktiv = btn.dataset.einheit === neu
      btn.setAttribute("aria-pressed", aktiv)
      ;["bg-white", "text-gray-900", "shadow-sm"].forEach(c => btn.classList.toggle(c, aktiv))
      ;["text-gray-600", "hover:text-gray-900"].forEach(c => btn.classList.toggle(c, !aktiv))
    })
    this.calculate()
  }

  // Eingabe in der Einheit, in der gerechnet wird (Einkommensgrenze: Jahr, sonst Monat)
  wert(input) {
    const feld = input.closest("[data-basis]")
    const betrag = this.num(input)
    if (!feld || feld.dataset.einheit === feld.dataset.basis) return betrag
    return feld.dataset.einheit === "jahr" ? betrag / 12 : betrag * 12
  }

  // Deutsche Schreibweise: „120.000“, „5.800,50“; ein einzelner Punkt mit 1 bis 2 Nachkommastellen gilt als Dezimalpunkt
  num(input) {
    const raw = (input.value || "").replace(/[\s€]/g, "")
    let normalized
    if (raw.includes(",")) normalized = raw.replace(/\./g, "").replace(",", ".")
    else if (/^\d{1,3}(\.\d{3})+$/.test(raw)) normalized = raw.replace(/\./g, "")
    else normalized = raw
    const value = parseFloat(normalized)
    return isNaN(value) ? 0 : Math.max(value, 0)
  }
}
